import { parseAndSanitizeMarkdown } from './markdownParser';
import TurndownService from 'turndown';
import { gfm, tables } from 'turndown-plugin-gfm';
import DOMPurify from 'dompurify';
/**
 * Inserts HTML content into the Word document at the current cursor selection.
 * This function uses the Office.js API and will only work when run inside a Word Add-in.
 * @param {string} html The HTML content to insert.
 * @returns {Promise<void>} A promise that resolves when the insertion is complete.
 * @throws {Error} Throws an error if not in a Word environment or if the insertion fails.
 */
export async function insertContentIntoWord(html) {
  // First, check if we are actually inside an Office Add-in.
  if (!window.Office || !window.Word) {
    const errorMessage = "This feature is only available within a Microsoft Word Add-in.";
    console.error(errorMessage);
    // We throw an error so the calling component knows it failed.
    throw new Error(errorMessage);
  }

  const styledHtml = `<style>
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid black; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                        img { max-width: 100%; height: auto; display: block; margin: 10px auto; }
                      </style>
                      ${html}
                      <p>&nbsp;</p>`;

  try {
    // Use the Word.run API to interact with the document.
    await window.Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.insertHtml(styledHtml, window.Word.InsertLocation.replace);
      await context.sync();
    });
  } catch (error) {
    console.error("Error during content insertion:", error);
    // Re-throw the error with a more user-friendly message.
    throw new Error("Could not insert content. Please ensure the document is editable and try again.");
  }
}




/**
 * Reads all the text from the currently open Word document.
 * @returns {Promise<string>} The document text.
 */
export const readEntireDocument = async () => {
  try {
    return await Word.run(async (context) => {
      // Get the body of the document
      const body = context.document.body;
      body.load("text");
      
      // Synchronize the document state to execute the queued commands
      await context.sync();
      
      return body.text;
    });
  } catch (error) {
    console.error("Error reading document:", error);
    throw new Error("Could not read the current Word document.");
  }
};

/**
 * Reads only the text the user has currently highlighted/selected.
 * @returns {Promise<string>} The selected text.
 */
export const readSelectedText = async () => {
  try {
    return await Word.run(async (context) => {
      // Get the current selection
      const selection = context.document.getSelection();
      
      selection.load("text");
      await context.sync();
      
      return selection.text;
    });
  } catch (error) {
    console.error("Error reading selection:", error);
    throw new Error("Could not read the selected text.");
  }
};


/**
 * Reads the entire body of the Word document and returns it as an HTML string.
 * This preserves structure, tables, and embeds images as Base64 data.
 * @returns {Promise<string>} A promise that resolves with the HTML content of the document.
 */
export const readEntireDocumentAsHtml = async () => {
  if (!window.Word) {
    throw new Error("This feature is only available in Word.");
  }

  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      
      // Queue a command to get the body's content as HTML.
      const bodyHtml = body.getHtml();
      
      // Synchronize to execute the command and retrieve the result.
      await context.sync();
      
      return bodyHtml.value;
    });
  } catch (error) {
    console.error("Error reading document as HTML:", error);
    throw new Error("Could not read the current Word document as HTML.");
  }
};

/**
 * [MODIFIED] Reads the entire Word document, cleans it, and converts its content to Markdown format.
 * This function handles headings, paragraphs, lists, tables, and images.
 * @returns {Promise<string>} A promise resolving with the document's Markdown content.
 */
// export const readEntireDocumentAsMarkdown = async () => {
//  if (!window.Word) {
//   console.warn("readEntireDocumentAsMarkdown: Not in a Word environment.");
//   return "";
//  }

//  try {
//   // 1. Get the raw, "dirty" HTML from the Word document.
//   const dirtyHtml= await Word.run(async (context) => {
//       const body = context.document.body;
      
//       // Queue a command to get the body's content as HTML.
//       const bodyHtml = body.getHtml();
      
//       // Synchronize to execute the command and retrieve the result.
//       await context.sync();
      
//       return bodyHtml.value;
//     });  
//  // const dirtyHtml = await readEntireDocumentAsHtml();
//   if (!dirtyHtml) {
//    return "";
//   }
//   console.log("html:",dirtyHtml)
//   // 2. Sanitize and clean the HTML with DOMPurify. This is a critical step.
//   const cleanHtml = DOMPurify.sanitize(dirtyHtml, {
//       ALLOWED_TAGS: [
//         'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br',
//         'strong', 'b', 'em', 'i', 'u', 's', 'strike',
//         'ul', 'ol', 'li', 'a', 'img',
//         'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td'
//       ],
//       ALLOWED_ATTR: ['href', 'src', 'alt', 'title']
//   });

//   // 3. Initialize Turndown service for HTML to Markdown conversion.
//   const turndownService = new TurndownService({
//    headingStyle: 'atx',
//    hr: '---',
//    bulletListMarker: '*',
//    codeBlockStyle: 'fenced',
//   });

//   // 4. Use the GFM plugin to handle tables, strikethrough, etc.
//   turndownService.use(gfm);

//   // 5. Convert the CLEAN HTML to Markdown and return it.
//   const markdown = turndownService.turndown(cleanHtml);
//   return markdown;

//  } catch (error) {
//   // This will catch the error thrown from readEntireDocumentAsHtml
//   console.error("Error in Markdown conversion pipeline:", error.message);
//   throw new Error("Could not convert the Word document to Markdown.");
//  }
// };

/**
 * Aggressively cleans Microsoft Word's messy "Mso" HTML.
 * Fixes headings, flattens merged tables, and deletes hidden comment blocks.
 */
function cleanWordHtml(rawHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');

  // 1. DESTROY WORD COMMENTS & FOOTERS ENTIRELY
  doc.querySelectorAll(
    '[style*="comment-list"], ' +
    '[class*="msocom"], ' +
    '[id^="_com_"], ' +
    '[id^="_ftn"], ' +
    '[name^="_com_"], ' +
    '[name^="_ftn"]'
  ).forEach(el => el.remove());

  // 2. UNWRAP TOC & ANCHOR TAGS (Fixes the "pprovals" missing heading issue)
  // Instead of deleting the tag (which deletes the text), we unwrap it.
  doc.querySelectorAll('a[name^="_Toc"], a[name^="_msoanchor"]').forEach(el => {
    const parent = el.parentNode;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });

  // 3. RESOLVE TRACKED CHANGES
  doc.querySelectorAll('del, .msoDel').forEach(el => el.remove()); // Remove deleted text
  doc.querySelectorAll('ins, .msoIns').forEach(el => {             // Keep inserted text
    const parent = el.parentNode;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });

  // 4. FIX TABLES FOR TURNDOWN
  doc.querySelectorAll('table').forEach(table => {
    // A. Remove ALL attributes (Strips rowspan and colspan which break Turndown)
    const tableElements = table.querySelectorAll('table, thead, tbody, tr, th, td');
    tableElements.forEach(el => {
      while (el.attributes.length > 0) el.removeAttribute(el.attributes[0].name);
    });

    // B. Force a <thead> (Required for Markdown tables)
    let thead = table.querySelector('thead');
    if (!thead) {
      const firstRow = table.querySelector('tr');
      if (firstRow) {
        thead = doc.createElement('thead');
        thead.appendChild(firstRow);
        table.insertBefore(thead, table.firstChild);
        
        // Convert first row <td> to <th>
        firstRow.querySelectorAll('td').forEach(td => {
          const th = doc.createElement('th');
          th.innerHTML = td.innerHTML;
          td.parentNode.replaceChild(th, td);
        });
      }
    }

    // C. Flatten Cells (Markdown tables break if cells contain <p>, <div>, or \n)
    table.querySelectorAll('th, td').forEach(cell => {
      // Replace block tags with spaces
      cell.innerHTML = cell.innerHTML.replace(/<(p|div|br|ul|ol|li)[^>]*>/gi, ' ');
      cell.innerHTML = cell.innerHTML.replace(/<\/(p|div|ul|ol|li)>/gi, ' ');
      // Condense whitespace
      cell.textContent = cell.textContent.replace(/[\r\n]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
      // Prevent completely empty cells
      if (!cell.textContent) cell.textContent = ' '; 
    });
  });

  // 5. STRIP REMAINING INLINE STYLES (Prevents parser confusion)
  doc.querySelectorAll('*').forEach(el => {
    el.removeAttribute('class');
    el.removeAttribute('style');
    el.removeAttribute('lang');
    el.removeAttribute('id');
    el.removeAttribute('width');
    el.removeAttribute('valign');
    el.removeAttribute('align');
  });

  // 6. REMOVE EMPTY SPACING PARAGRAPHS
  doc.querySelectorAll('p, span, div').forEach(el => {
    if (el.textContent.trim() === '' && !el.querySelector('img')) {
      el.remove();
    }
  });

  let cleanBody = doc.body.innerHTML;
  
  // 7. CLEAN UP SYSTEM VARIABLES
  cleanBody = cleanBody.replace(/(Start_it\d+\$|End_it\d+\$)/g, '');

  return cleanBody;
}

export const readEntireDocumentAsMarkdown = async () => {
  if (!window.Word) {
    throw new Error("This feature is only available in Word.");
  }

  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      
      // 1. Get raw MSO HTML
      const bodyHtml = body.getHtml();
      await context.sync();
      
      // 2. Aggressively clean the HTML
      const cleanedHtml = cleanWordHtml(bodyHtml.value);

      // 3. Initialize Turndown
      const turndownService = new TurndownService({
        headingStyle: 'atx',
        bulletListMarker: '-',
        codeBlockStyle: 'fenced'
      });

      // Enable table support
      turndownService.use(tables);

      // 4. Fix Word's "Fake" Lists
      turndownService.addRule('word-fake-lists', {
        filter: 'p',
        replacement: function (content) {
          return '\n\n' + content.replace(/^(\d+)\\([.)])/gm, '$1$2') + '\n\n';
        }
      });

      // 5. Strip out any remaining naked links
      turndownService.addRule('empty-links', {
        filter: 'a',
        replacement: function (content) {
          return content ? content : '';
        }
      });

      // 6. Convert to Markdown
      let finalMarkdown = turndownService.turndown(cleanedHtml);
      
      // 7. FINAL PASS SWEEPS
      // Remove Turndown's escaping of standard characters
      finalMarkdown = finalMarkdown.replace(/\\([.\-*_>])/g, '$1');

      // NUKE LEFTOVER WORD COMMENT MARKERS: Removes things like [AY12] or [YA{74]
      finalMarkdown = finalMarkdown.replace(/\[[A-Za-z]+\{?\d+\]/g, '');

      return finalMarkdown.trim();
    });
  } catch (error) {
    console.error("Error reading document and converting to Markdown:", error);
    try {
      return await readEntireDocument(); // Ultimate fallback to plain text
    } catch (fallbackError) {
      throw new Error("Could not read the current Word document.");
    }
  }
};
/**
 * Finds all occurrences of a specific string in the document and replaces them.
 * @param {string} textToFind The exact text to search for.
 * @param {string} textToReplace The new text to insert.
 * @returns {Promise<number>} A promise that resolves with the number of replacements made.
 */
export const findAndReplaceText = async (textToFind, textToReplace) => {
  if (!window.Word) {
    throw new Error("This feature is only available in Word.");
  }

  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      
      // Set up the search options. You can configure this (e.g., matchCase).
      const searchOptions = { matchCase: false, matchWholeWord: false };
      const searchResults = body.search(textToFind, searchOptions);
      
      // Load the search results to see how many there are.
      context.load(searchResults, "items");
      await context.sync();

      const replacementCount = searchResults.items.length;
      if (replacementCount === 0) {
        console.warn(`Text to find was not found in the document: "${textToFind}"`);
        return 0; // Return 0 if no matches were found.
      }
      
      // Queue a replacement command for each found item.
      searchResults.items.forEach(item => {
        item.insertText(textToReplace, Word.InsertLocation.replace);
      });
      
      // Execute all the queued replacement commands.
      await context.sync();
      
      return replacementCount;
    });
  } catch (error) {
    console.error("Error during find and replace:", error);
    throw new Error("Could not perform the find and replace operation in the document.");
  }
};

/**
 * Replaces the currently selected text in the Word document.
 * @param {} newText The new text to insert in place of the selection.
 * @returns {Promise<void>} A promise that resolves when the replacement is complete.
 */
export const replaceSelectedText = async (newText) => {
  if (!window.Word) {
    throw new Error("This feature is only available in Word.");
  }

  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      
      // Replace the selected content with the new text
      selection.insertText(newText, Word.InsertLocation.replace);
      
      await context.sync();
    });
  } catch (error) {
    console.error("Error replacing selected text:", error);
    throw new Error("Could not replace the selected text. Please ensure you have something selected.");
  }
};

/**
 * Finds a section by its heading text and replaces its entire content.
 * A section is defined as the content from the start of the heading
 * to the start of the next heading of the same or higher level, or the end of the document.
 * 
 * @param {} headingText The exact text of the heading to find (e.g., "Introduction").
 * @param {} newHtml The new HTML content to replace the section with.
 * @returns {Promise<boolean>} A promise that resolves to true if the replacement was successful, false otherwise.
 */
export const findAndReplaceSection = async (headingText, newHtml) => {
  if (!window.Word) {
    throw new Error("This feature is only available in Microsoft Word.");
  }

  try {
    return await Word.run(async (context) => {
      // Find the starting heading of the section.
      const searchOptions = { matchCase: false, matchWholeWord: false };
      const startHeadingResults = context.document.body.search(headingText, searchOptions);
      context.load(startHeadingResults, 'items');
      await context.sync();

      if (startHeadingResults.items.length === 0) {
        console.warn(`Could not find the section heading: "${headingText}"`);
        return false; // Indicate that the section was not found.
      }
      const startHeadingRange = startHeadingResults.items[0];

      // Find the *next* heading in the document to determine the section's end.
      // We search for common heading styles. You can customize this list.
      const headingStyles = ["Heading 1", "Heading 2", "Heading 3"];
      let nextHeadingRange = null;
      let documentRanges = [];

      for (const style of headingStyles) {
        const results = context.document.body.search(style, { matchWildcards: false, isStyle: true });
        context.load(results, 'items');
        documentRanges.push(results);
      }
      await context.sync();

      // Combine all found headings and sort them by their position in the document.
      let allHeadings = [];
      for(const result of documentRanges) {
          allHeadings.push(...result.items);
      }
      allHeadings.sort((a, b) => a.getRange('Start')._KeepInContent(b.getRange('Start')).m_results[0] ? -1 : 1);

      // Find the first heading that comes *after* our startHeadingRange.
      for (const heading of allHeadings) {
          const comparison = startHeadingRange.compareLocationWith(heading);
          context.load(comparison);
          await context.sync();
          if (comparison.value === 'After') {
              nextHeadingRange = heading;
              break;
          }
      }

      // Define the full range of the section to be replaced.
      let sectionToReplace;
      if (nextHeadingRange) {
        // The section is from the start of our heading to the start of the next one.
        sectionToReplace = startHeadingRange.getRange().expandTo(nextHeadingRange.getRange('Start'));
      } else {
        // No next heading found; the section goes to the end of the document.
        sectionToReplace = startHeadingRange.getRange().expandTo(context.document.body.getRange('End'));
      }

      // 4. Replace the entire section range with the new HTML.
      sectionToReplace.insertHtml(newHtml, Word.InsertLocation.replace);
      await context.sync();
      
      return true; // Indicate success.
    });
  } catch (error) {
    console.error("Error during find and replace section:", error);
    throw new Error(`Failed to replace the "${headingText}" section.`);
  }
};

export async function autoReplaceMultipleSections(instructions) {
  try {
    return await Word.run(async (context) => {
      let replacedCount = 0;

      for (const instruction of instructions) {
        const targetTitle = instruction.target_section_title;
        const newTitle = instruction.new_header_text;
        const newParagraphs = instruction.new_body_paragraphs;

        // 1. Load paragraphs (reloaded inside the loop because indices change after edits)
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text, items/style, items/parentTableOrNullObject");
        await context.sync();  

        let headerIndex = -1;

        // 2. Find the header paragraph
        for (let i = 0; i < paragraphs.items.length; i++) {
          if (paragraphs.items[i].text.toLowerCase().includes(targetTitle.toLowerCase())) {
            headerIndex = i;
            break;
          }
        }

        if (headerIndex === -1) {
          console.warn(`Section "${targetTitle}" not found. Skipping.`);
          continue; 
        }

        const headerPara = paragraphs.items[headerIndex];
         
        // 3. Replace the Header Text safely
        if (newTitle) {
          const cleanNewTitle = newTitle.replace(/^[\d\w]+\.?\s+/, "").trim();
          if (cleanNewTitle.length > 0) {
            // Using "Replace" string instead of Enum prevents API crashes
            headerPara.insertText(cleanNewTitle, "Replace");
          }
        }

        // 4. Find where the section ends
        let sectionEndIndex = headerIndex;
        for (let i = headerIndex + 1; i < paragraphs.items.length; i++) {
          if (paragraphs.items[i].style.includes("Heading") || paragraphs.items[i].style.includes("Title")) {
            break;
          }
          sectionEndIndex = i;
        }

        // 5. BULLETPROOF DELETION (Fixes empty tables & prevents double-delete crashes)
        for (let i = sectionEndIndex; i > headerIndex; i--) {
          try {
            const para = paragraphs.items[i];
            
            // If the paragraph is inside a table, delete the ENTIRE table grid
            if (!para.parentTableOrNullObject.isNullObject) {
              para.parentTableOrNullObject.delete();
              
              // CRITICAL: Sync immediately! This tells Word the table is gone,
              // preventing the loop from accidentally trying to delete it again.
              await context.sync(); 
            } else {
              para.delete();
            }
          } catch (e) {
            // Silently ignore if the paragraph was already destroyed by a table deletion
          }
        }
        await context.sync(); // Final sync for any remaining standard paragraphs

        // 6. SAFE HTML/MARKDOWN INSERTION (Fixes the List Item crash)
        if (Array.isArray(newParagraphs) && newParagraphs.length > 0) {
          // Loop backwards so we always insert "After" the header, maintaining 1, 2, 3 order
          for (let i = newParagraphs.length - 1; i >= 0; i--) {
            const text = newParagraphs[i];
            if (!text || text.trim() === "") continue;

            // CRITICAL FIX: Create a temporary blank paragraph first.
            // This safely breaks us out of the numbered list structure.
            let dummyPara = headerPara.insertParagraph(" ", "After");
             dummyPara.styleBuiltIn = Word.BuiltInStyleName.normal; 
            try {
              // Convert Markdown (like **bold** or tables) to safe HTML
              const safeHtml = parseAndSanitizeMarkdown(text);
              
              if (safeHtml && safeHtml.trim().length > 0) {
                // Replace the blank paragraph with our rich HTML
                dummyPara.insertHtml(safeHtml, "Replace");
              } else {
                dummyPara.insertText(text, "Replace");
              }
            } catch (htmlError) {
              console.warn("HTML insertion failed, falling back to plain text:", htmlError);
              // Ultimate Fallback: Just insert standard text
              dummyPara.insertText(text, "Replace");
            }
          }
        }
        await context.sync();
         
        replacedCount++; 
      }

      return replacedCount; 
    });
  } catch (error) {
    console.error("Error in autoReplaceMultipleSections:", error);
    throw error;
  }
}

export const replaceSelectedContent = async (markdownText) => {
  if (!window.Word) {
    throw new Error("This feature is only available in Word.");
  }

  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      
      // 1. Ensure the backend didn't send an undefined or non-string value
      const textToInsert = typeof markdownText === 'string' ? markdownText : String(markdownText || "");

      if (!textToInsert.trim()) {
        console.warn("Backend sent empty text for replacement.");
        return; // Exit safely if there's nothing to insert
      }
      
      // 2. Safely parse the markdown
      let safeHtml = "";
      try {
        safeHtml = parseAndSanitizeMarkdown(textToInsert);
      } catch (parseError) {
        console.warn("Markdown parsing failed, falling back to raw text:", parseError);
      }
      
      // 3. Insert the content
      if (safeHtml && safeHtml.trim().length > 0) {
        try {
          // Use the exact Enum that your other working functions use
          selection.insertHtml(safeHtml, Word.InsertLocation.replace);
        } catch (htmlError) {
          console.warn("insertHtml failed, falling back to insertText:", htmlError);
          // Fallback if Word rejects the HTML structure
          selection.insertText(textToInsert, Word.InsertLocation.replace);
        }
      } else {
        // Fallback if HTML is empty
        selection.insertText(textToInsert, Word.InsertLocation.replace);
      }
      
      await context.sync();
    });
  } catch (error) {
    console.error("Error replacing selected content:", error);
    throw new Error("Could not replace the selected text. Please ensure you have text highlighted.");
  }
};


/**
 * Extracts enhanced document context with rich formatting metadata [P#|Style].
 * @returns {Promise<{paragraphs: Array, formattedText: string, sectionCount: number}>}
 */
export async function extractEnhancedDocumentContext() {
  if (!window.Word) {
    throw new Error("This feature is only available in Word.");
  }

  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      const paragraphs = body.paragraphs;

      paragraphs.load("items");
      await context.sync();

      for (const para of paragraphs.items) {
        para.load("text, style, listItemOrNullObject, parentTableOrNullObject, parentTableCellOrNullObject");
      }
      await context.sync();

      for (const para of paragraphs.items) {
        if (!para.listItemOrNullObject.isNullObject) {
          para.listItemOrNullObject.load("level, listString");
        }
        if (!para.parentTableCellOrNullObject.isNullObject) {
          para.parentTableCellOrNullObject.load("rowIndex, cellIndex");
        }
      }
      await context.sync();

      const enhancedParagraphs = [];
      let currentSection = null;
      let currentSubSection = null;
      let sectionCounter = 0;
      let lastListLevel = -1;
      let sectionStack = [];

      for (let i = 0; i < paragraphs.items.length; i++) {
        const para = paragraphs.items[i];
        const text = para.text || "";
        const style = para.style || "Normal";

        const metaParts = [style];

        let isListItem = false;
        let listLevel = -1;
        let listString = "";

        if (!para.listItemOrNullObject.isNullObject) {
          isListItem = true;
          listLevel = para.listItemOrNullObject.level || 0;
          listString = para.listItemOrNullObject.listString || "";

          const isNumbered = style.toLowerCase().includes("number") ||
            style.toLowerCase().includes("list number") ||
            /^\d+[.)]/.test(listString);
          const listType = isNumbered ? "ListNumber" : "ListBullet";

          metaParts[0] = listType;
          metaParts.push(`L:${listLevel}`);
        }

        let isInTable = false;
        if (!para.parentTableCellOrNullObject.isNullObject) {
          isInTable = true;
          const rowIndex = para.parentTableCellOrNullObject.rowIndex || 0;
          const cellIndex = para.parentTableCellOrNullObject.cellIndex || 0;
          metaParts.push(`T:${rowIndex},${cellIndex}`);
        }

        let sectionMarker = "";
        if (isListItem && !isInTable) {
          if (listLevel === 0) {
            sectionCounter++;
            currentSection = String(sectionCounter);
            currentSubSection = null;
            sectionStack = [currentSection];
            sectionMarker = "§";
            lastListLevel = listLevel;
          } else if (listLevel > lastListLevel) {
            const parentSection = sectionStack[sectionStack.length - 1] || currentSection;
            currentSubSection = `${parentSection}.${listLevel}`;
            sectionStack.push(currentSubSection);
            sectionMarker = "§";
            lastListLevel = listLevel;
          } else if (listLevel <= lastListLevel && listLevel > 0) {
            while (sectionStack.length > listLevel + 1) {
              sectionStack.pop();
            }
            const parentSection = sectionStack[0] || currentSection;
            currentSubSection = `${parentSection}.${listLevel}`;
            sectionStack[listLevel] = currentSubSection;
            sectionMarker = "§";
            lastListLevel = listLevel;
          }

          if (sectionMarker) {
            metaParts.push(sectionMarker);
          }
        } else if (!isListItem && !isInTable && currentSection) {
          const belongsTo = currentSubSection || currentSection;
          metaParts.push(`§${belongsTo}`);
        }

        const metaString = metaParts.join("|");
        const enhancedLine = `[P${i + 1}|${metaString}] ${text}`;

        enhancedParagraphs.push({
          index: i + 1,
          text: text,
          style: style,
          enhancedLine: enhancedLine
        });
      }

      return {
        paragraphs: enhancedParagraphs,
        formattedText: enhancedParagraphs.map(p => p.enhancedLine).join("\n"),
        sectionCount: sectionCounter
      };
    });
  } catch (error) {
    console.error("Error extracting enhanced context:", error);
    // Fallback to basic text if it fails
    const basicText = await readEntireDocument(); 
    return { paragraphs: [], formattedText: basicText, sectionCount: 0 };
  }
}
// export async function autoReplaceMultipleSections(instructions) {
//   try {
//     return await Word.run(async (context) => {
//       let replacedCount = 0;

//       // Loop through all the sections the backend wants to replace
//       for (const instruction of instructions) {
//         const targetTitle = instruction.target_section_title;
//         const newTitle = instruction.new_header_text;
//         const newParagraphs = instruction.new_body_paragraphs;

//         // 1. Load paragraphs (we reload inside the loop because indices change after edits)
//         const paragraphs = context.document.body.paragraphs;
//          paragraphs.load("items/text, items/style, items/parentTableOrNullObject");
//         await context.sync();  

//         let headerIndex = -1;

//         // 2. Find the header paragraph
//         for (let i = 0; i < paragraphs.items.length; i++) {
//           if (paragraphs.items[i].text.toLowerCase().includes(targetTitle.toLowerCase())) {
//             headerIndex = i;
//             break;
//           }
//         }

//         // If a section is not found, skip it and move to the next one
//         if (headerIndex === -1) {
//           console.warn(`Section "${targetTitle}" not found. Skipping.`);
//           continue; 
//         }

//         const headerPara = paragraphs.items[headerIndex];
        
//         // 3. Replace the Header Text
//         if (newTitle) {
//           // Try to preserve numbering if it exists (e.g. "1. ")
//           const cleanNewTitle = newTitle.replace(/^[\d\w]+\.?\s+/, "");
//           headerPara.insertText(cleanNewTitle, Word.InsertLocation.replace);
         
//         }

//         // 4. Find where the section ends (stops at the next Heading)
//         let sectionEndIndex = headerIndex;
//         for (let i = headerIndex + 1; i < paragraphs.items.length; i++) {
//           if (paragraphs.items[i].style.includes("Heading") || paragraphs.items[i].style.includes("Title")) {
//             break;
//           }
//           sectionEndIndex = i;
//         }

//         // 5. Delete old paragraphs (loop backwards to avoid index shifting issues)
//         for (let i = sectionEndIndex; i > headerIndex; i--) {
//           paragraphs.items[i].delete();
//         }
//         await context.sync();

//         // 6. Insert new paragraphs
//         let insertAfterPara = headerPara;
//         for (const text of newParagraphs) {
//           insertAfterPara = insertAfterPara.insertParagraph(text, Word.InsertLocation.after);
//         }
//         await context.sync();
        
//         replacedCount++; // Track successful replacements
//       }

//       return replacedCount; // Return how many sections were successfully updated
//     });
//   } catch (error) {
//     console.error("Error in autoReplaceMultipleSections:", error);
//     throw error;
//   }
// }